import { Observable, TimeoutError, timeout } from 'rxjs';
import { environment } from 'src/environments/environment';

/** Plazo por defecto si el entorno no define `loginTimeoutMs`. */
export const DEFAULT_LOGIN_TIMEOUT_MS = 15000;

export const LOGIN_FAILURE_MESSAGES = {
  timeout: 'El inicio de sesión demoró demasiado. Por favor intenta de nuevo.',
  network: 'No se pudo conectar con el servidor. Revisa tu conexión e intenta de nuevo.',
  unavailable:
    'El servicio de inicio de sesión no está disponible. Por favor intenta de nuevo en unos minutos.',
  credentials: 'Usuario o password incorrectos',
  emailNotVerified:
    'Tu email no ha sido verificado. Por favor verifica tu email antes de iniciar sesión.',
  unknown: 'No se pudo iniciar sesión. Por favor intenta de nuevo.',
} as const;

export type LoginFailureKind =
  | 'timeout'
  | 'network'
  | 'unavailable'
  | 'credentials'
  | 'email_not_verified'
  | 'unknown';

export interface LoginFailure {
  kind: LoginFailureKind;
  message: string;
  title?: string;
}

/** Tiempo máximo de la autenticación (ms). Configurable por entorno. */
export function loginTimeoutMs(): number {
  const configured = Number(
    (environment as { loginTimeoutMs?: number }).loginTimeoutMs
  );
  if (!Number.isFinite(configured) || configured <= 0) {
    return DEFAULT_LOGIN_TIMEOUT_MS;
  }
  return configured;
}

/**
 * Corta la espera de login. Al cumplirse el plazo se desuscribe la fuente,
 * lo que aborta el HTTP en curso (HttpClient no tiene timeout propio).
 */
export function boundLoginAttempt<T>(
  source: Observable<T>,
  timeoutMs: number = loginTimeoutMs()
): Observable<T> {
  const ms = Number.isFinite(timeoutMs) && timeoutMs > 0 ? timeoutMs : DEFAULT_LOGIN_TIMEOUT_MS;
  return source.pipe(timeout({ first: ms }));
}

export function isLoginTimeoutError(err: unknown): boolean {
  return err instanceof TimeoutError || (err instanceof Error && err.name === 'TimeoutError');
}

/**
 * Traduce fallos de login a un mensaje fijo.
 * No reexpone status, URL, cuerpo ni stack del servidor.
 */
export function classifyLoginFailure(err: unknown): LoginFailure {
  if (isLoginTimeoutError(err)) {
    return {
      kind: 'timeout',
      message: LOGIN_FAILURE_MESSAGES.timeout,
      title: 'Tiempo de espera agotado',
    };
  }

  const description = readErrorDescription(err);
  if (
    description === 'EMAIL_NOT_VERIFIED' ||
    description.includes('Email no verificado')
  ) {
    return {
      kind: 'email_not_verified',
      message: LOGIN_FAILURE_MESSAGES.emailNotVerified,
      title: 'Email no verificado',
    };
  }

  const status = readStatus(err);
  if (status === 0) {
    return { kind: 'network', message: LOGIN_FAILURE_MESSAGES.network, title: 'Sin conexión' };
  }
  if (status === 408) {
    return {
      kind: 'timeout',
      message: LOGIN_FAILURE_MESSAGES.timeout,
      title: 'Tiempo de espera agotado',
    };
  }
  if (status != null && status >= 500) {
    return {
      kind: 'unavailable',
      message: LOGIN_FAILURE_MESSAGES.unavailable,
      title: 'Servicio no disponible',
    };
  }
  if (status === 400) {
    return { kind: 'credentials', message: LOGIN_FAILURE_MESSAGES.credentials };
  }
  return { kind: 'unknown', message: LOGIN_FAILURE_MESSAGES.unknown };
}

function readStatus(err: unknown): number | null {
  if (!err || typeof err !== 'object' || !('status' in err)) {
    return null;
  }
  const status = (err as { status?: unknown }).status;
  return typeof status === 'number' ? status : null;
}

function readErrorDescription(err: unknown): string {
  if (!err || typeof err !== 'object' || !('error' in err)) {
    return '';
  }
  const body = (err as { error?: unknown }).error;
  if (!body || typeof body !== 'object') {
    return '';
  }
  const record = body as Record<string, unknown>;
  const description = record['error_description'] ?? record['errorDescription'];
  return typeof description === 'string' ? description : '';
}
